import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaEye, 
  FaChartBar, 
  FaSpinner,
  FaArrowLeft,
  FaCog,
  FaTable,
  FaChartPie,
  FaChartLine,
  FaChartArea
} from 'react-icons/fa';
import DataApi from '../../api/DataApi';
import './css/WidgetManager.css';

const WidgetManager = () => {
  const { dashboardId } = useParams();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [widgets, setWidgets] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingWidget, setEditingWidget] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    report_id: '',
    chart_type: 'bar',
    sort_order: 0,
    column_span: 6, // Default to 6 columns to match CSS expectations
    row_span: 4, // Default to 4 rows to match CSS expectations
    chart_config: {}
  });

  useEffect(() => {
    if (dashboardId) {
      loadDashboard();
      loadWidgets();
      loadReports();
    }
  }, [dashboardId]);

  const loadDashboard = async () => {
    try {
      const dashboardApi = new DataApi('dashboard');
      const response = await dashboardApi.fetch(`/${dashboardId}`);
      
      if (response.data.success) {
        setDashboard(response.data.data);
      } else {
        setError('Failed to load dashboard');
      }
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError('Error loading dashboard. Please try again.');
    }
  };

  const loadWidgets = async () => {
    try {
      const dashboardApi = new DataApi('dashboard');
      const response = await dashboardApi.fetchAll(`/${dashboardId}/widgets`);
      
      if (response.data.success) {
        setWidgets(response.data.data);
      } else {
        setError('Failed to load widgets');
      }
    } catch (err) {
      console.error('Error loading widgets:', err);
      setError('Error loading widgets. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadReports = async () => {
    try {
      const reportApi = new DataApi('reports'); // Fixed: use 'reports' (plural) to match backend route
      const response = await reportApi.fetchAll();
      
      if (response.data.success) {
        setReports(response.data.data);
      }
    } catch (err) {
      console.error('Error loading reports:', err);
    }
  };

  const handleCreateWidget = async (e) => {
    e.preventDefault();
    try {
      const dashboardApi = new DataApi('dashboard');
      const response = await dashboardApi.post(`/${dashboardId}/widgets`, formData);
      
      if (response.data.success) {
        setShowCreateModal(false);
        setFormData({
          name: '',
          report_id: '',
          chart_type: 'bar',
          sort_order: 0,
          column_span: 6, // Default to 6 columns to match CSS expectations
          row_span: 4, // Default to 4 rows to match CSS expectations
          chart_config: {}
        });
        loadWidgets(); // Reload the list
      } else {
        setError('Failed to create widget');
      }
    } catch (err) {
      console.error('Error creating widget:', err);
      setError('Error creating widget. Please try again.');
    }
  };

  const handleEditWidget = async (e) => {
    e.preventDefault();
    try {
      const dashboardApi = new DataApi('dashboard');
      const response = await dashboardApi.update(`/widgets/${editingWidget.id}`, formData);
      
      if (response.data.success) {
        setShowEditModal(false);
        setEditingWidget(null);
        setFormData({
          name: '',
          report_id: '',
          chart_type: 'bar',
          sort_order: 0,
          column_span: 6, // Default to 6 columns to match CSS expectations
          row_span: 4, // Default to 4 rows to match CSS expectations
          chart_config: {}
        });
        loadWidgets(); // Reload the list
      } else {
        setError('Failed to update widget');
      }
    } catch (err) {
      console.error('Error updating widget:', err);
      setError('Error updating widget. Please try again.');
    }
  };

  const handleDeleteWidget = async (widgetId) => {
    if (!window.confirm('Are you sure you want to delete this widget? This action cannot be undone.')) {
      return;
    }

    try {
      const dashboardApi = new DataApi('dashboard');
      const response = await dashboardApi.delete(`/widgets/${widgetId}`);
      
      if (response.data.success) {
        loadWidgets(); // Reload the list
      } else {
        setError('Failed to delete widget');
      }
    } catch (err) {
      console.error('Error deleting widget:', err);
      setError('Error deleting widget. Please try again.');
    }
  };

  const openEditModal = (widget) => {
    setEditingWidget(widget);
    setFormData({
      name: widget.name || '',
      report_id: widget.report_id || '',
      chart_type: widget.chart_type || 'bar',
      sort_order: widget.sort_order || 0,
      column_span: widget.column_span || 6, // Default to 6 columns to match CSS expectations
      row_span: widget.row_span || 4, // Default to 4 rows to match CSS expectations
      chart_config: widget.chart_config || {}
    });
    setShowEditModal(true);
  };

  const openCreateModal = () => {
    setFormData({
      name: '',
      report_id: '',
      chart_type: 'bar',
      sort_order: widgets.length,
      column_span: 6, // Default to 6 columns to match CSS expectations
      row_span: 4, // Default to 4 rows to match CSS expectations
      chart_config: {}
    });
    setShowCreateModal(true);
  };

  const closeModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setEditingWidget(null);
    setFormData({
      name: '',
      report_id: '',
      chart_type: 'bar',
      sort_order: 0,
      column_span: 6, // Default to 6 columns to match CSS expectations
      row_span: 4, // Default to 4 rows to match CSS expectations
      chart_config: {}
    });
  };

  const getChartIcon = (chartType) => {
    switch (chartType) {
      case 'bar':
        return <FaChartBar />;
      case 'line':
        return <FaChartLine />;
      case 'pie':
        return <FaChartPie />;
      case 'area':
        return <FaChartArea />;
      case 'table':
        return <FaTable />;
      default:
        return <FaChartBar />;
    }
  };

  const getChartTypeLabel = (chartType) => {
    switch (chartType) {
      case 'bar':
        return 'Bar Chart';
      case 'line':
        return 'Line Chart';
      case 'pie':
        return 'Pie Chart';
      case 'area':
        return 'Area Chart';
      case 'table':
        return 'Table';
      default:
        return 'Chart';
    }
  };

  if (loading) {
    return (
      <div className="widget-manager">
        <div className="widget-loading-container">
          <FaSpinner className="widget-spinner" />
          <p>Loading widgets...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="widget-manager">
      {/* Header */}
      <div className="widget-header">
        <div className="widget-header-content">
          <div className="widget-header-title">
            <button 
              className="widget-back-btn"
              onClick={() => navigate('/dashboard')}
            >
              <FaArrowLeft />
            </button>
            <FaChartBar className="widget-header-icon" />
            <div className="widget-title-info">
              <h1>Widget Manager</h1>
              {dashboard && (
                <p className="widget-dashboard-name">
                  Dashboard: {dashboard.label || dashboard.name}
                </p>
              )}
            </div>
          </div>
          <button 
            className="widget-create-btn"
            onClick={openCreateModal}
          >
            <FaPlus />
            Add Widget
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="widget-error-message">
          <p>{error}</p>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Widgets Grid */}
      <div className="widget-grid-container">
        {widgets.length === 0 ? (
          <div className="widget-empty-state">
            <FaChartBar className="widget-empty-icon" />
            <h3>No widgets found</h3>
            <p>Get started by adding your first widget to this dashboard.</p>
            <button className="widget-create-btn" onClick={openCreateModal}>
              <FaPlus />
              Add Widget
            </button>
          </div>
        ) : (
          <div className="widget-grid">
            {widgets
              .sort((a, b) => a.sort_order - b.sort_order)
              .map((widget) => (
                <div 
                  key={widget.id} 
                  className={`widget-card widget-${widget.chart_type}`}
                  style={{
                    gridColumn: `span ${widget.column_span}`,
                    gridRow: `span ${widget.row_span}`
                  }}
                >
                  <div className="widget-card-header">
                    <div className="widget-card-info">
                      <div className="widget-icon">
                        {getChartIcon(widget.chart_type)}
                      </div>
                      <div className="widget-details">
                        <h3 className="widget-name">{widget.name}</h3>
                        <p className="widget-type">{getChartTypeLabel(widget.chart_type)}</p>
                        {widget.report_name && (
                          <p className="widget-report">Report: {widget.report_name}</p>
                        )}
                      </div>
                    </div>
                    <div className="widget-actions">
                      <button
                        className="widget-action-btn widget-edit-btn"
                        onClick={() => openEditModal(widget)}
                        title="Edit Widget"
                      >
                        <FaEdit />
                      </button>
                      <button
                        className="widget-action-btn widget-delete-btn"
                        onClick={() => handleDeleteWidget(widget.id)}
                        title="Delete Widget"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                  <div className="widget-preview">
                    <div className="widget-preview-placeholder">
                      <div className="widget-preview-icon">
                        {getChartIcon(widget.chart_type)}
                      </div>
                      <p>Widget Preview</p>
                      <small>Chart will be rendered here</small>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Create Widget Modal */}
      {showCreateModal && (
        <div className="widget-modal-overlay">
          <div className="widget-modal">
            <div className="widget-modal-header">
              <h2>Add New Widget</h2>
              <button className="widget-close-btn" onClick={closeModals}>×</button>
            </div>
            <form onSubmit={handleCreateWidget} className="widget-modal-form">
              <div className="widget-form-group">
                <label htmlFor="name">Widget Name *</label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Enter widget name"
                />
              </div>
              <div className="widget-form-group">
                <label htmlFor="report_id">Report *</label>
                <select
                  id="report_id"
                  value={formData.report_id}
                  onChange={(e) => setFormData({ ...formData, report_id: e.target.value })}
                  required
                >
                  <option value="">Select a report</option>
                  {reports.map((report) => (
                    <option key={report.id} value={report.id}>
                      {report.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="widget-form-group">
                <label htmlFor="chart_type">Chart Type *</label>
                <select
                  id="chart_type"
                  value={formData.chart_type}
                  onChange={(e) => setFormData({ ...formData, chart_type: e.target.value })}
                  required
                >
                  <option value="bar">Bar Chart</option>
                  <option value="line">Line Chart</option>
                  <option value="pie">Pie Chart</option>
                  <option value="area">Area Chart</option>
                  <option value="table">Table</option>
                </select>
              </div>
              <div className="widget-form-row">
                <div className="widget-form-group">
                  <label htmlFor="sort_order">Sort Order</label>
                  <input
                    type="number"
                    id="sort_order"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                    min="0"
                  />
                </div>
                <div className="widget-form-group">
                  <label htmlFor="column_span">Column Span</label>
                  <select
                    id="column_span"
                    value={formData.column_span}
                    onChange={(e) => setFormData({ ...formData, column_span: parseInt(e.target.value) })}
                  >
                    <option value={1}>1 Column</option>
                    <option value={2}>2 Columns</option>
                    <option value={3}>3 Columns</option>
                    <option value={4}>4 Columns</option>
                    <option value={6}>6 Columns (Default)</option>
                    <option value={12}>12 Columns (Full Width)</option>
                  </select>
                </div>
                <div className="widget-form-group">
                  <label htmlFor="row_span">Row Span</label>
                  <select
                    id="row_span"
                    value={formData.row_span}
                    onChange={(e) => setFormData({ ...formData, row_span: parseInt(e.target.value) })}
                  >
                    <option value={1}>1 Row</option>
                    <option value={2}>2 Rows</option>
                    <option value={3}>3 Rows</option>
                    <option value={4}>4 Rows (Default)</option>
                    <option value={6}>6 Rows</option>
                  </select>
                </div>
              </div>
              <div className="widget-modal-actions">
                <button type="button" className="widget-cancel-btn" onClick={closeModals}>
                  Cancel
                </button>
                <button type="submit" className="widget-save-btn">
                  Create Widget
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Widget Modal */}
      {showEditModal && (
        <div className="widget-modal-overlay">
          <div className="widget-modal">
            <div className="widget-modal-header">
              <h2>Edit Widget</h2>
              <button className="widget-close-btn" onClick={closeModals}>×</button>
            </div>
            <form onSubmit={handleEditWidget} className="widget-modal-form">
              <div className="widget-form-group">
                <label htmlFor="edit-name">Widget Name *</label>
                <input
                  type="text"
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Enter widget name"
                />
              </div>
              <div className="widget-form-group">
                <label htmlFor="edit-report_id">Report *</label>
                <select
                  id="edit-report_id"
                  value={formData.report_id}
                  onChange={(e) => setFormData({ ...formData, report_id: e.target.value })}
                  required
                >
                  <option value="">Select a report</option>
                  {reports.map((report) => (
                    <option key={report.id} value={report.id}>
                      {report.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="widget-form-group">
                <label htmlFor="edit-chart_type">Chart Type *</label>
                <select
                  id="edit-chart_type"
                  value={formData.chart_type}
                  onChange={(e) => setFormData({ ...formData, chart_type: e.target.value })}
                  required
                >
                  <option value="bar">Bar Chart</option>
                  <option value="line">Line Chart</option>
                  <option value="pie">Pie Chart</option>
                  <option value="area">Area Chart</option>
                  <option value="table">Table</option>
                </select>
              </div>
              <div className="widget-form-row">
                <div className="widget-form-group">
                  <label htmlFor="edit-sort_order">Sort Order</label>
                  <input
                    type="number"
                    id="edit-sort_order"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                    min="0"
                  />
                </div>
                <div className="widget-form-group">
                  <label htmlFor="edit-column_span">Column Span</label>
                  <select
                    id="edit-column_span"
                    value={formData.column_span}
                    onChange={(e) => setFormData({ ...formData, column_span: parseInt(e.target.value) })}
                  >
                    <option value={1}>1 Column</option>
                    <option value={2}>2 Columns</option>
                    <option value={3}>3 Columns</option>
                    <option value={4}>4 Columns</option>
                    <option value={6}>6 Columns (Default)</option>
                    <option value={12}>12 Columns (Full Width)</option>
                  </select>
                </div>
                <div className="widget-form-group">
                  <label htmlFor="edit-row_span">Row Span</label>
                  <select
                    id="edit-row_span"
                    value={formData.row_span}
                    onChange={(e) => setFormData({ ...formData, row_span: parseInt(e.target.value) })}
                  >
                    <option value={1}>1 Row</option>
                    <option value={2}>2 Rows</option>
                    <option value={3}>3 Rows</option>
                    <option value={4}>4 Rows (Default)</option>
                    <option value={6}>6 Rows</option>
                  </select>
                </div>
              </div>
              <div className="widget-modal-actions">
                <button type="button" className="widget-cancel-btn" onClick={closeModals}>
                  Cancel
                </button>
                <button type="submit" className="widget-save-btn">
                  Update Widget
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WidgetManager;
